import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SportsSoccerOutlinedIcon from "@mui/icons-material/SportsSoccerOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { useHistory, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomers } from "hooks/useGestionCustomers";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { useGestionAccess } from "hooks/useGestionAccess";
import { canAccessArea } from "utils/gestion-access";
import { PUSH_AREA } from "components/pages/Gestion/push-broadcast-helpers";

export const isGestionDrawerItemActive = (pathname, item) => {
  if (item.exclude && pathname.startsWith(item.exclude)) return false;
  if (item.exact) return pathname === item.link;
  if (item.nested) return pathname === item.link || pathname.startsWith(`${item.link}/`);
  return pathname === item.link;
};

// Items without `area` are open to every Gestión user. Visibility is decided by filterDrawerGroups.
export const buildDrawerGroups = (customerId) => [
  {
    label: "General",
    items: [
      {
        labelId: "gestion.nav.dashboard",
        link: GESTION_ROUTES.dashboard(customerId),
        icon: <DashboardOutlinedIcon />,
        exact: true,
      },
      {
        labelId: "gestion.nav.tournaments",
        link: GESTION_ROUTES.tournaments(customerId),
        icon: <EmojiEventsOutlinedIcon />,
        nested: true,
        exclude: GESTION_ROUTES.tournamentsCreate(customerId),
        area: "tournaments",
      },
    ],
  },
  {
    label: "Operación",
    items: [
      {
        labelId: "gestion.nav.teams",
        link: GESTION_ROUTES.teams(customerId),
        icon: <GroupsOutlinedIcon />,
        nested: true,
        area: "tournaments",
      },
      {
        labelId: "gestion.nav.players",
        link: GESTION_ROUTES.players(customerId),
        icon: <PersonOutlinedIcon />,
        area: "tournaments",
      },
      {
        labelId: "gestion.nav.matches",
        link: GESTION_ROUTES.matches(customerId),
        icon: <SportsSoccerOutlinedIcon />,
        area: "tournaments",
      },
      {
        labelId: "gestion.nav.financials",
        link: GESTION_ROUTES.financials(customerId),
        icon: <AttachMoneyOutlinedIcon />,
        area: "finance",
      },
      {
        labelId: "gestion.nav.notifications",
        link: GESTION_ROUTES.notifications(customerId),
        icon: <NotificationsOutlinedIcon />,
        area: PUSH_AREA,
      },
    ],
  },
  {
    label: "Configuración",
    items: [
      {
        labelId: "gestion.nav.settings",
        link: GESTION_ROUTES.settings(customerId),
        icon: <SettingsOutlinedIcon />,
        nested: true,
        exclude: GESTION_ROUTES.settingsApp(customerId),
        area: "tournaments",
      },
      {
        labelId: "gestion.nav.settingsApp",
        link: GESTION_ROUTES.settingsApp(customerId),
        icon: <PaletteOutlinedIcon />,
        area: "manager",
      },
      {
        labelId: "gestion.nav.migration",
        link: GESTION_ROUTES.migration(customerId),
        icon: <SwapHorizOutlinedIcon />,
        area: "reva-admin",
      },
    ],
  },
];

// access: the object returned by useGestionAccess (or resolveGestionAccess).
export const filterDrawerGroups = (groups, access) =>
  groups
    .map((group) => ({ ...group, items: group.items.filter((item) => canAccessArea(access, item.area)) }))
    .filter((group) => group.items.length > 0);

const GestionDrawer = ({ currentPathname, onNavigate, collapsed = false }) => {
  const { gestionCustomers, loading } = useGestionCustomers();
  const { customerId, customer, setSelectedGestionCustomer } = useGestionCustomer();
  const access = useGestionAccess();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const pathname = currentPathname || location.pathname;
  const selectValue = customer ? String(customer.id) : "";

  const handleCustomerChange = (nextCustomerId) => {
    const nextCustomer = gestionCustomers.find((item) => String(item.id) === String(nextCustomerId));
    if (!nextCustomer) return;
    setSelectedGestionCustomer(nextCustomer);
    const suffix = window.location.pathname.replace(/^\/admin\/gestion\/\d+/, "");
    history.push(GESTION_ROUTES.dashboard(nextCustomer.id) + (suffix || ""));
  };

  const groups = customerId ? buildDrawerGroups(customerId) : [];

  const goTo = (link) => {
    history.push(link);
    onNavigate?.();
  };

  const renderNavigationItem = (item) => {
    const active = Boolean(pathname) && isGestionDrawerItemActive(pathname, item);
    const label = intl.formatMessage({ id: item.labelId });
    const button = (
      <ListItemButton
        component="button"
        type="button"
        selected={active}
        onClick={() => goTo(item.link)}
        sx={{ justifyContent: collapsed ? "center" : "flex-start", px: collapsed ? 1.5 : 2 }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34, mr: collapsed ? 0 : 1, justifyContent: "center" }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && <ListItemText primary={label} />}
      </ListItemButton>
    );

    return (
      <Tooltip key={item.link} title={collapsed ? label : ""} placement="right">
        {button}
      </Tooltip>
    );
  };

  if (loading && gestionCustomers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {!collapsed && (
        <Box sx={{ px: 1, pb: 2 }}>
          <Select
            fullWidth
            size="small"
            value={selectValue}
            onChange={(event) => handleCustomerChange(event.target.value)}
            displayEmpty
            disabled={gestionCustomers.length === 0}
          >
            {gestionCustomers.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.gestion_access === "payment_required"
                  ? `${item.name} (${intl.formatMessage({ id: "gestion.paymentRequired.badge" })})`
                  : item.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <List disablePadding>
        {filterDrawerGroups(groups, access).map((group) => (
          <React.Fragment key={group.label}>
            {!collapsed && (
              <ListSubheader component="div" disableSticky sx={{ bgcolor: "transparent" }}>
                {intl.formatMessage({ id: group.label })}
              </ListSubheader>
            )}
            {group.items.map(renderNavigationItem)}
            <Divider sx={{ my: 1 }} />
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default GestionDrawer;
